import React, { useState, useRef, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSearch, FiChevronDown, FiCheck, FiCpu, FiX, FiDollarSign } = FiIcons;

const SearchableModelSelect = ({ models = [], value, onChange, placeholder = 'Select a model...' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selected = models.find(m => m.model === value);
  const filtered = models.filter(m => 
    m.name.toLowerCase().includes(query.toLowerCase()) || 
    m.model.toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={containerRef} className="relative w-full">
      <button 
        type="button" 
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between gap-3 p-3.5 rounded-xl border bg-sonar-card text-left transition-all outline-none ${open ? 'border-sonar-green ring-1 ring-sonar-green' : 'border-sonar-border hover:border-sonar-green/50 shadow-sm'}`}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-8 h-8 rounded-lg bg-sonar-green/10 flex items-center justify-center shrink-0">
            <SafeIcon icon={FiCpu} className="text-sonar-green text-sm" />
          </div>
          {selected ? (
            <div className="min-w-0">
              <div className="text-xs font-bold text-sonar-text truncate">{selected.name}</div>
              <div className="text-[10px] font-mono text-sonar-text-muted truncate">{selected.model}</div>
            </div>
          ) : (
            <span className="text-sm text-sonar-text-muted font-mono">{placeholder}</span>
          )}
        </div>
        <SafeIcon icon={FiChevronDown} className={`text-sonar-text-muted transition-transform ${open ? 'rotate-180 text-sonar-green' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-[100] mt-2 w-full bg-sonar-card border border-sonar-border rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl">
          <div className="p-3 border-b border-sonar-border bg-sonar-bg/30">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-sonar-green/50 text-sm" />
              <input 
                ref={inputRef}
                type="text" 
                value={query} 
                onChange={e => setQuery(e.target.value)}
                placeholder="Search engine models..." 
                className="w-full pl-10 pr-8 py-2.5 bg-sonar-bg border border-sonar-border rounded-xl text-xs text-sonar-text placeholder-slate-500 font-mono outline-none focus:border-sonar-green transition-all"
              />
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto">
            {filtered.map(m => {
              const isSelected = m.model === value;
              return (
                <button 
                  key={m.model} 
                  type="button" 
                  onClick={() => { onChange(m.model); setOpen(false); }}
                  className={`w-full flex items-center gap-4 px-4 py-3.5 text-left transition-all border-b border-sonar-border/30 last:border-0 group ${isSelected ? 'bg-sonar-green/10' : 'hover:bg-sonar-green/5'}`}
                >
                  <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${isSelected ? 'border-sonar-green bg-sonar-green text-black' : 'border-sonar-border group-hover:border-sonar-green/50'}`}>
                    {isSelected && <SafeIcon icon={FiCheck} className="text-[10px]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-bold truncate ${isSelected ? 'text-sonar-green' : 'text-sonar-text group-hover:text-sonar-green'}`}>
                      {m.name}
                    </div>
                    <div className="text-[10px] font-mono text-sonar-text-muted truncate">{m.model}</div>
                  </div>
                  {m.cost && (
                    <div className="text-right shrink-0">
                      <div className="text-[9px] font-black text-sonar-green uppercase tracking-tighter">1M Tokens</div>
                      <div className="text-[10px] font-mono text-sonar-text-muted">${m.cost.input} / ${m.cost.output}</div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableModelSelect;